import { BranchIcon, ChartIcon, CreateOrderIcon, FeedbackIcon, HistoryIcon, InfoIcon, MenuIcon, QueueIcon, SearchIcon, SessionsIcon, SettingsIcon, StoreIcon, TableIcon, UsersIcon } from '@/components/icons'
import type { RestaurantScope } from '@/features/back-office/scope'
import type { NavTarget } from '@/lib/nav'
import { canManageBranch, canManageRestaurant } from '@/lib/roles'

/**
 * Destinations inside one restaurant, filtered by the caller's role. Inside a branch the
 * operation pages lead, then the branch management ones; manager and above also get the one
 * link back to the restaurant. Outside a branch, the restaurant pages (manager and above only).
 * Admin and staff enter and switch branches through the landing pages (2026-09-14).
 */
export function restaurantNav({ restaurantSlug, context, branch }: RestaurantScope): NavTarget[] {
  if (!context) return []
  // 2026-09-12: a deactivated restaurant refuses every page, so the nav offers none.
  if (context.settings.deactivated_at != null) return []
  const params = { restaurantSlug }
  const items: NavTarget[] = []
  // `branch` is the URL's branch only when the context lists it: a link the backend would refuse is no link.
  // G9/G12: the operation pages come first — every role; inside a branch they fill the bottom bar (T13).
  if (branch !== undefined) {
    items.push({
      key: 'queue',
      to: '/$restaurantSlug/$branchSlug/manage/queue',
      params: { ...params, branchSlug: branch.slug },
      icon: QueueIcon,
      label: 'nav.queue',
      group: 'operation',
    })
    items.push({
      key: 'sessions',
      to: '/$restaurantSlug/$branchSlug/manage/sessions',
      params: { ...params, branchSlug: branch.slug },
      icon: SessionsIcon,
      label: 'nav.sessions',
      group: 'operation',
    })
    items.push({
      key: 'create-order',
      to: '/$restaurantSlug/$branchSlug/manage/create-order',
      params: { ...params, branchSlug: branch.slug },
      icon: CreateOrderIcon,
      label: 'nav.createOrder',
      group: 'operation',
    })
    items.push({
      key: 'search',
      to: '/$restaurantSlug/$branchSlug/manage/search',
      params: { ...params, branchSlug: branch.slug },
      icon: SearchIcon,
      label: 'nav.search',
      group: 'operation',
    })
  }
  // 2026-09-14: settings leads as the page the Branches list opens; the rest by how often a shift touches them.
  if (branch !== undefined && canManageBranch(context.role)) {
    items.push({
      key: 'branch-settings',
      to: '/$restaurantSlug/$branchSlug/manage/settings',
      params: { ...params, branchSlug: branch.slug },
      icon: SettingsIcon,
      label: 'nav.branchSettings',
      group: 'branch',
    })
    items.push({
      key: 'branch-menu',
      to: '/$restaurantSlug/$branchSlug/manage/menu',
      params: { ...params, branchSlug: branch.slug },
      icon: MenuIcon,
      label: 'nav.branchMenu',
      group: 'branch',
    })
    items.push({
      key: 'statistics',
      to: '/$restaurantSlug/$branchSlug/manage/statistics',
      params: { ...params, branchSlug: branch.slug },
      icon: ChartIcon,
      label: 'nav.statistics',
      group: 'branch',
    })
    items.push({
      key: 'branch-staff',
      to: '/$restaurantSlug/$branchSlug/manage/staff',
      params: { ...params, branchSlug: branch.slug },
      icon: UsersIcon,
      label: 'nav.staff',
      group: 'branch',
    })
    items.push({
      key: 'branch-tables',
      to: '/$restaurantSlug/$branchSlug/manage/tables',
      params: { ...params, branchSlug: branch.slug },
      icon: TableIcon,
      label: 'nav.tables',
      group: 'branch',
    })
  }
  // H2: manager and above only — the PRD keeps admins out of branch info.
  if (branch !== undefined && canManageRestaurant(context.role)) {
    items.push({
      key: 'branch-info',
      to: '/$restaurantSlug/$branchSlug/manage/info',
      params: { ...params, branchSlug: branch.slug },
      icon: InfoIcon,
      label: 'nav.branchInfo',
      group: 'branch',
    })
    // 2026-09-14: inside a branch the restaurant group is only the way back to the restaurant.
    items.push({
      key: 'manage-restaurant',
      to: '/$restaurantSlug/manage/branches',
      params,
      icon: StoreIcon,
      label: 'nav.manageRestaurant',
      group: 'restaurant',
    })
  }
  if (branch === undefined && canManageRestaurant(context.role)) {
    // F4: the branch list leads — the hub a manager moves through and the landing (2026-09-14).
    items.push({
      key: 'branches',
      to: '/$restaurantSlug/manage/branches',
      params,
      icon: BranchIcon,
      label: 'nav.branches',
      group: 'restaurant',
    })
    // F3: recurring work, so it keeps a bottom-bar slot when More appears.
    items.push({
      key: 'menu',
      to: '/$restaurantSlug/manage/menu',
      params,
      icon: MenuIcon,
      label: 'nav.menu',
      group: 'restaurant',
    })
    // I3: what customers wrote — the owner's phone check, so it takes the third bar slot.
    items.push({
      key: 'feedback',
      to: '/$restaurantSlug/manage/feedback',
      params,
      icon: FeedbackIcon,
      label: 'nav.feedback',
      group: 'restaurant',
    })
    items.push({
      key: 'accounts',
      to: '/$restaurantSlug/manage/accounts',
      params,
      icon: UsersIcon,
      label: 'nav.accounts',
      group: 'restaurant',
    })
    // I2: the restaurant's change record, same audience as settings.
    items.push({
      key: 'audit-log',
      to: '/$restaurantSlug/manage/audit-log',
      params,
      icon: HistoryIcon,
      label: 'nav.auditLog',
      group: 'restaurant',
    })
    items.push({
      key: 'settings',
      to: '/$restaurantSlug/manage/settings',
      params,
      icon: SettingsIcon,
      label: 'nav.settings',
      group: 'restaurant',
    })
  }
  // 2026-09-12: a closed branch keeps its statistics and nothing else of the branch.
  const closed = branch?.deactivated_at != null
  return closed ? items.filter((item) => item.group === 'restaurant' || item.key === 'statistics') : items
}
